/**
 * Mahjong — sound palette.
 *
 * Aesthetic: real ceramic/bamboo tiles on a wood tea table. Every sound is a
 * TEXTURE with a hard little body, never a beep:
 *   - tap    — a free tile selected: a soft, bright ceramic touch (a short
 *              high knock + a breath of noise). The lightest sound.
 *   - clack  — the satisfying TILE-ON-TILE match: two quick hard knocks a hair
 *              apart, a pitch a touch higher than `tap`, with a woody body. The
 *              signature sound of the game.
 *   - sink   — the matched pair settling away: a low rounded wooden "tock" that
 *              fades, paired with the clear.
 *   - nudge  — a soft "not those two": a low, rounded thud with a tiny downward
 *              bend. Never a buzzer, never red.
 *   - shuffle— the reshuffle wash: a swell of many small wooden ticks (a noise
 *              band) like tiles being washed across the table.
 *   - hint   — a calm jade chime: a single soft bell tone, inviting not urgent.
 *   - win    — the flourish: a warm wooden sweep under a slow, bright ascending
 *              pentatonic figure on bell-like tones (~1.5s) — the payoff.
 *   - nav    — hub / page transitions: a single soft wooden brush swell.
 *
 * Everything routes through `audio.playIfNotMuted`, so the global mute gate is
 * respected automatically. These syntheses are bespoke to Mahjong — no key,
 * waveform, or envelope is shared with Solitaire or Five.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type MahjongSoundName =
  | 'tap'
  | 'clack'
  | 'sink'
  | 'nudge'
  | 'shuffle'
  | 'hint'
  | 'win'
  | 'nav';

export const soundHaptic: Partial<Record<MahjongSoundName, HapticStyle>> = {
  tap: 'light',
  clack: 'success',
  sink: 'medium',
  nudge: 'warning',
  shuffle: 'light',
  hint: 'light',
  win: 'success',
  nav: 'light',
};

// ---------------------------------------------------------------------------
// Jitter helpers — keep repeated tiles from sounding identical.
// ---------------------------------------------------------------------------

/** ±cents pitch jitter as a multiplier. */
function centsJitter(cents = 16): number {
  const c = (Math.random() * 2 - 1) * cents;
  return Math.pow(2, c / 1200);
}

/** ±dB level jitter as a linear multiplier. */
function dbJitter(db = 2): number {
  const d = (Math.random() * 2 - 1) * db;
  return Math.pow(10, d / 20);
}

// ---------------------------------------------------------------------------
// Texture primitives
// ---------------------------------------------------------------------------

/**
 * A hard little tile KNOCK — a pitched ceramic/wood body with a fast downward
 * pitch sweep plus a noise transient. `pitch` low = a wooden "tock"; high = a
 * ceramic "tick". This is the heart of the mahjong feel.
 */
function knock(opts: {
  pitch: number;
  durationMs?: number;
  peak?: number;
  noise?: number;
  delayMs?: number;
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime + (opts.delayMs ?? 0) / 1000;
  const dur = (opts.durationMs ?? 64) / 1000;
  const pitch = opts.pitch * centsJitter();
  const peak = (opts.peak ?? 0.2) * dbJitter();

  // Two detuned bodies give the ceramic "double" ring without sounding pure.
  const osc = audio.makeOscillator(ctx, { freq: pitch, type: 'triangle' });
  osc.frequency.setValueAtTime(pitch, t);
  osc.frequency.exponentialRampToValueAtTime(pitch * 0.55, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.02);

  const osc2 = audio.makeOscillator(ctx, { freq: pitch * 2.01, type: 'sine' });
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.0001, t);
  g2.gain.exponentialRampToValueAtTime(peak * 0.4, t + 0.003);
  g2.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.6);
  audio.connectThrough(osc2, g2, master);
  osc2.start(t);
  osc2.stop(t + dur + 0.02);

  const noiseAmt = opts.noise ?? 0.4;
  if (noiseAmt > 0) {
    const buf = audio.noiseBuffer(ctx, { lengthMs: 22 });
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = pitch * 3.4;
    bp.Q.value = 1.2;
    const { node: ng } = audio.envelope(ctx, {
      attack: 0.001,
      release: 0.03,
      peak: peak * noiseAmt,
    });
    audio.connectThrough(src, bp, ng, master);
    src.start(t);
    src.stop(t + 0.05);
  }
}

/** A band of filtered noise that swells & fades — the "tiles washed across the
 *  table" texture under the shuffle and the nav brush. */
function woodWash(opts: {
  durationMs: number;
  centerHz: number;
  q?: number;
  peak?: number;
  sweepTo?: number;
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = opts.durationMs / 1000;
  const peak = (opts.peak ?? 0.12) * dbJitter();

  const buf = audio.noiseBuffer(ctx, { lengthMs: opts.durationMs + 60, shaping: 1.5 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(opts.centerHz, t);
  if (opts.sweepTo) bp.frequency.exponentialRampToValueAtTime(opts.sweepTo, t + dur);
  bp.Q.value = opts.q ?? 0.7;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2400;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.05,
    sustain: 0.6,
    release: dur,
    peak,
  });
  audio.connectThrough(src, bp, lp, g, master);
  src.start(t);
  src.stop(t + dur + 0.06);
}

/** A soft bell-like tone — used for the hint chime and the win figure. */
function bell(opts: { freq: number; durationMs: number; peak?: number; delayMs?: number }): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime + (opts.delayMs ?? 0) / 1000;
  const dur = opts.durationMs / 1000;
  const peak = opts.peak ?? 0.12;

  // A bell = a fundamental + a slightly inharmonic partial, both decaying.
  const osc = audio.makeOscillator(ctx, { freq: opts.freq, type: 'sine' });
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.05);

  const osc2 = audio.makeOscillator(ctx, { freq: opts.freq * 2.76, type: 'sine' });
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.0001, t);
  g2.gain.exponentialRampToValueAtTime(peak * 0.28, t + 0.01);
  g2.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.5);
  audio.connectThrough(osc2, g2, master);
  osc2.start(t);
  osc2.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Concrete sounds
// ---------------------------------------------------------------------------

function synthTap(): void {
  knock({ pitch: 520, durationMs: 52, peak: 0.13, noise: 0.5 });
}

function synthClack(): void {
  // The signature match: two hard knocks a hair apart — tile meeting tile.
  knock({ pitch: 660, durationMs: 60, peak: 0.2, noise: 0.55 });
  knock({ pitch: 590, durationMs: 70, peak: 0.17, noise: 0.45, delayMs: 42 });
}

function synthSink(): void {
  // The pair settling away — a low rounded wooden tock that fades.
  knock({ pitch: 240, durationMs: 110, peak: 0.18, noise: 0.3 });
}

function synthNudge(): void {
  // A soft, rounded "no" — low and short with a tiny downward bend.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 184, type: 'sine' });
  osc.frequency.linearRampToValueAtTime(150, t + 0.17);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.006,
    decay: 0.04,
    sustain: 0.5,
    release: 0.17,
    peak: 0.14,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.24);
}

function synthShuffle(): void {
  // The wash of many tiles — a noise swell plus a scatter of tiny ticks.
  woodWash({ durationMs: 620, centerHz: 720, sweepTo: 1100, peak: 0.12, q: 0.6 });
  for (let i = 0; i < 7; i += 1) {
    knock({ pitch: 460 + Math.random() * 260, durationMs: 36, peak: 0.06, noise: 0.7, delayMs: 40 + i * 70 });
  }
}

function synthHint(): void {
  // A calm jade chime — inviting, not urgent.
  bell({ freq: 587.33, durationMs: 520, peak: 0.1 }); // D5
  bell({ freq: 880.0, durationMs: 460, peak: 0.06, delayMs: 70 }); // A5
}

function synthWin(): void {
  // The flourish — a warm wooden sweep under a slow ascending pentatonic figure
  // on bell tones. Open, generous, unhurried. ~1.5s, the biggest beat.
  woodWash({ durationMs: 1300, centerHz: 760, sweepTo: 1500, peak: 0.1, q: 0.5 });
  // C–D–E–G–A–C(oct) — a major pentatonic climb (no kitsch, no minor).
  const notes = [
    { f: 523.25, d: 0 }, // C5
    { f: 587.33, d: 150 }, // D5
    { f: 659.26, d: 300 }, // E5
    { f: 783.99, d: 470 }, // G5
    { f: 880.0, d: 640 }, // A5
    { f: 1046.5, d: 850 }, // C6
  ];
  notes.forEach((n, i) => bell({ freq: n.f, durationMs: 1000 - i * 70, peak: 0.12, delayMs: n.d }));
  bell({ freq: 1318.51, durationMs: 1100, peak: 0.06, delayMs: 1050 }); // E6 shimmer close
}

function synthNav(): void {
  woodWash({ durationMs: 300, centerHz: 820, sweepTo: 1300, peak: 0.11, q: 0.6 });
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class MahjongSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: MahjongSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'tap':
          synthTap();
          return;
        case 'clack':
          synthClack();
          return;
        case 'sink':
          synthSink();
          return;
        case 'nudge':
          synthNudge();
          return;
        case 'shuffle':
          synthShuffle();
          return;
        case 'hint':
          synthHint();
          return;
        case 'win':
          synthWin();
          return;
        case 'nav':
          synthNav();
          return;
      }
    });
  }
}

export const sound = new MahjongSound();
